import React from 'react';
import { useTranslation } from 'react-i18next';
import { rowActivation } from './rowActivation';
import { AttributePill, TagPill } from './pills';
import { optionToken, optClasses } from './optionColor';

/**
 * CardRow (phone/tablet) + GridRow (desktop table). They SHARE the pill primitives and both take their
 * attribute-column list as a PROP (`columns`) — P2 passes a fixed default; P3's saved views drive it, so
 * nothing here is hardcoded to "these two". Per erratum E-1: CardRow always opens the SHEET, GridRow always
 * opens the POPOVER (each passes its shape up via onEditAttr). AC-P2.4: both render the SAME data + colours.
 */
export interface LeadRow {
  id: string; leadId: string;
  name: string; phone?: string | null; email?: string | null;
  clientStatus?: string | null; qualificationLevel?: string | null;
  tags?: string[]; lastActivityAt?: string | null; conversationsCount?: number;
}
export interface AttrColumn { key: keyof LeadRow & string; labelKey: string; }
export type EditShape = 'sheet' | 'popover';
export interface RowCallbacks {
  selected?: boolean;
  onOpenPerson: (lead: LeadRow) => void;
  onEditAttr: (lead: LeadRow, col: AttrColumn, anchor: HTMLElement, shape: EditShape) => void;
  onOpenAll?: (lead: LeadRow) => void; // '+N' → full person attribute sheet (§5.1)
  onRemove?: (leadId: string) => void; // un-list a lead from the rolodex (AC18); preserved from the old list
  onConvert?: (leadId: string) => void; // B5 (D3): the convert verb on the row — leads grid only
}

function attrValue(lead: LeadRow, key: string): string | null {
  const v = (lead as Record<string, unknown>)[key];
  return v == null ? null : String(v);
}
export function initials(name: string): string { return (name || '·').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '·'; }
export const Avatar: React.FC<{ lead: LeadRow; size: number }> = ({ lead, size }) => {
  const { bg, fg } = optClasses(optionToken(lead.leadId)); // deterministic per lead
  return <span style={{ width: size, height: size }} className={`inline-flex items-center justify-center rounded-xl text-[11px] font-bold ${bg} ${fg}`}>{initials(lead.name)}</span>;
};
// Relative recency (§5.1): "hoy HH:MM" / "ayer HH:MM" / else a short date — scannable, not a same-date column.
export function recency(iso: string | null | undefined, t: (k: string, o?: any) => string): string {
  if (!iso) return '—';
  const d = new Date(iso); const now = new Date();
  const hm = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const days = Math.floor((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86400000);
  if (days <= 0) return t('leadsGrid.recency.today', { time: hm });
  if (days === 1) return t('leadsGrid.recency.yesterday', { time: hm });
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

// ── CardRow (phone/tablet) — max 3 pills + '+N'; card body opens the person, pills open the SHEET ──
const PILL_BUDGET = 3;
export const CardRow: React.FC<{ lead: LeadRow; columns: AttrColumn[] } & RowCallbacks> = ({ lead, columns, selected, onOpenPerson, onEditAttr, onOpenAll, onRemove, onConvert }) => {
  const { t } = useTranslation('common');
  const shown = columns.slice(0, PILL_BUDGET);
  const overflow = columns.length - shown.length;
  return (
    <div {...rowActivation(() => onOpenPerson(lead))} onClick={() => onOpenPerson(lead)}
      className={`flex gap-3 p-3 rounded-xl bg-surface border ${selected ? 'ring-2 ring-primary/15 border-primary/30' : 'border-border-neutral'} cursor-pointer`}>
      <Avatar lead={lead} size={38} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-text-strong truncate">{lead.name || '—'}</span>
          <span className="ms-auto text-[11px] text-text-muted shrink-0" dir="ltr">{recency(lead.lastActivityAt, t)}</span>
          {onConvert && <button data-testid="lead-convert" title={t('leadsGrid.convert')} className="text-[10px] text-primary font-bold shrink-0" onClick={(e) => { e.stopPropagation(); onConvert(lead.leadId || lead.id); }}>{t('leadsGrid.convert')}</button>}
          {onRemove && <button data-testid="lead-remove" title={t('leadsGrid.remove')} className="text-[10px] text-text-muted hover:text-red-600 font-bold shrink-0" onClick={(e) => { e.stopPropagation(); onRemove(lead.leadId || lead.id); }}>{t('leadsGrid.remove')}</button>}
        </div>
        <div className="text-xs text-text-muted truncate" dir="ltr">{lead.phone || lead.email || '—'}</div>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          {shown.map((col) => (
            <AttributePill key={col.key} value={attrValue(lead, col.key)} label={t(col.labelKey)} emptyText={t('customers.detail.setField', { field: t(col.labelKey) })}
              onOpen={(a) => onEditAttr(lead, col, a, 'sheet')} />
          ))}
          {(lead.tags ?? []).slice(0, 2).map((t) => <TagPill key={t} tag={t} />)}
          {overflow > 0 && (
            <button type="button" className="h-6 px-2 rounded-full text-[11px] font-semibold text-text-muted"
              onClick={(e) => { e.stopPropagation(); onOpenAll?.(lead); }}>+{overflow}</button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── GridRow (desktop table) — every column real, NO '+N'; name is the sticky first col; pills open the POPOVER ──
export const GridRow: React.FC<{ lead: LeadRow; columns: AttrColumn[] } & RowCallbacks> = ({ lead, columns, selected, onOpenPerson, onEditAttr, onRemove, onConvert }) => {
  const { t } = useTranslation('common');
  return (
  <tr tabIndex={0} onKeyDown={rowActivation(() => onOpenPerson(lead)).onKeyDown} className={`border-b border-border-neutral hover:bg-canvas cursor-pointer ${selected ? 'bg-canvas' : ''}`} onClick={() => onOpenPerson(lead)}>
    {/* sticky first column: inset-inline-start:0 (E-3) so it sticks to the correct edge in he-RTL */}
    <td className="sticky z-10 bg-surface p-4 [inset-inline-start:0]">
      <div className="flex items-center gap-2 min-w-0"><Avatar lead={lead} size={28} /><span className="text-sm font-semibold text-text-strong truncate">{lead.name || '—'}</span></div>
    </td>
    <td className="p-4 text-xs text-text-muted whitespace-nowrap" dir="ltr">{lead.phone || lead.email || '—'}</td>
    {columns.map((col) => (
      <td key={col.key} className="p-4 whitespace-nowrap">
        <AttributePill value={attrValue(lead, col.key)} label={t(col.labelKey)} emptyText={t('customers.detail.setField', { field: t(col.labelKey) })}
          onOpen={(a) => onEditAttr(lead, col, a, 'popover')} />
      </td>
    ))}
    <td className="p-4"><div className="flex flex-wrap gap-1.5">{(lead.tags ?? []).map((t) => <TagPill key={t} tag={t} />)}</div></td>
    <td className="p-4 text-[11px] text-text-muted whitespace-nowrap" dir="ltr">{recency(lead.lastActivityAt, t)}</td>
    <td className="p-4 whitespace-nowrap">
      <div className="flex items-center gap-3">
        {onConvert && <button data-testid="lead-convert" title={t('leadsGrid.convert')} className="text-[11px] text-primary font-bold" onClick={(e) => { e.stopPropagation(); onConvert(lead.leadId || lead.id); }}>{t('leadsGrid.convert')}</button>}
        {onRemove && <button data-testid="lead-remove" title={t('leadsGrid.remove')} className="text-[11px] text-text-muted hover:text-red-600 font-bold" onClick={(e) => { e.stopPropagation(); onRemove(lead.leadId || lead.id); }}>{t('leadsGrid.remove')}</button>}
      </div>
    </td>
  </tr>
  );
};
